/**
 * Password Setup Hook
 * 비밀번호 설정 관련 로직 관리
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { setupPassword } from '../api/passwordSetupApi';
import { PasswordSetupRequest } from '../types/passwordSetup';
import { setCookie } from '../../../core/utils';
import { useAuth } from '../../../core/contexts/AuthContext';

export const usePasswordSetup = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();
    const { setAuth } = useAuth();

    const handlePasswordSetup = useCallback(async (email: string, newPassword: string, confirmPassword: string, type: 'newPassword' | 'changePassword' = 'newPassword') => {
        setIsLoading(true);
        setError(null);

        // 이메일 유효성 검사 (email이 빈 문자열이면 비밀번호 찾기에서 온 경우이므로 스킵)
        if (email && email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email)) {
                setError('올바른 이메일 형식이 아닙니다.');
                setIsLoading(false);
                return;
            }
        }

        // 비밀번호 유효성 검사
        if (newPassword.length < 8) {
            setError('비밀번호는 8자리 이상이어야 합니다.');
            setIsLoading(false);
            return;
        }

        // 숫자, 영문, 특수문자 포함 여부 확인
        const hasNumber = /\d/.test(newPassword);
        const hasLetter = /[a-zA-Z]/.test(newPassword);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

        if (!hasNumber || !hasLetter || !hasSpecial) {
            setError('숫자, 영문, 특수문자를 포함해야 합니다.');
            setIsLoading(false);
            return;
        }

        // 비밀번호 확인
        if (newPassword !== confirmPassword) {
            setError('비밀번호가 일치하지 않습니다.');
            setIsLoading(false);
            return;
        }

        try {
            // 로그인 시 저장한 userId 가져오기 (로그인 페이지에서 입력한 userId)
            // 또는 쿠키나 다른 곳에서 가져올 수 있음
            // 일단 로그인 시 userId를 localStorage에 저장했다고 가정
            const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId') || '';
            
            if (!userId) {
                setError('사용자 정보를 찾을 수 없습니다. 다시 로그인해주세요.');
                navigate('/login');
                return;
            }

            const request: PasswordSetupRequest = {
                userId,
                email: email ? email.trim() : '', // 비밀번호 찾기에서 온 경우 빈 문자열
                password: newPassword,
                confirmPassword,
                type, // 'newPassword' 또는 'changePassword'
            };

            const response = await setupPassword(request);
            
            if (response.success) {
                // 토큰을 쿠키에 저장 (응답에 토큰이 있는 경우)
                if (response.accessToken) {
                    setCookie('accessToken', response.accessToken, {
                        expires: 1, // 24시간 (1일)
                        path: '/',
                        secure: window.location.protocol === 'https:',
                        sameSite: 'lax',
                    });
                }
                if (response.refreshToken) {
                    setCookie('refreshToken', response.refreshToken, {
                        expires: 1, // 24시간 (1일)
                        path: '/',
                        secure: window.location.protocol === 'https:',
                        sameSite: 'lax',
                    });
                }
                
                // 비밀번호 설정 성공 - userInfo를 localStorage에 저장 후 AuthContext 설정
                const userInfo: any = {
                    riderId: userId,
                    requiresPasswordSetup: false,
                };

                if (response.userName) {
                    userInfo.riderName = response.userName;
                }

                localStorage.setItem('userInfo', JSON.stringify(userInfo));

                // userId 정리
                localStorage.removeItem('userId');
                sessionStorage.removeItem('userId');

                // AuthContext 설정 (GuestRoute가 정상 로그인으로 인식하도록)
                setAuth(userId, 'RIDER', '');

                // 비밀번호 설정 성공 후 홈으로 이동
                navigate('/home');
            } else {
                setError(response.message || '비밀번호 설정에 실패했습니다.');
            }
        } catch (err: any) {
            setError(err.message || '비밀번호 설정 중 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    }, [navigate, setAuth]);

    return {
        handlePasswordSetup,
        isLoading,
        error,
    };
};
